/**
 * lib/pureSession.js — Sprint 3: modalità "pure session".
 *
 * Livello 2 di sicurezza identità:
 *   - Livello 0: tutto in IDB, niente PIN (default Sprint 2)
 *   - Livello 1: tutto in IDB, protetto da PIN (Sprint 3)
 *   - Livello 2: NIENTE in IDB. La mnemonic vive solo in memoria per la
 *     durata della sessione. Chiudi tab → tutto sparisce. (Sprint 3)
 *
 * Trade-off:
 *   + Massima sicurezza: nessuna traccia su disco
 *   + Protezione da forensic post-hoc
 *   - Devi reinserire le 24 parole ogni volta che apri l'app
 *   - Niente notifiche push offline (push richiede registrazione persistente)
 *
 * Uso tipico: giornalisti, attivisti, situazioni a rischio device-seizure.
 */

const PURE_SESSION_KEY = 'nullum.pureSession.enabled';

// In-memory state (vive finché la pagina è caricata)
let memoryKeystore = null;

export function isPureSessionEnabled() {
  return localStorage.getItem(PURE_SESSION_KEY) === 'true';
}

export function enablePureSession() {
  localStorage.setItem(PURE_SESSION_KEY, 'true');
  // Cancella IDB esistente
  return wipeIDB();
}

export function disablePureSession() {
  localStorage.setItem(PURE_SESSION_KEY, 'false');
  memoryKeystore = null;
}

/**
 * Carica le chiavi in memoria (chiamata all'unlock con mnemonic).
 * Le chiavi NON vengono mai persistite.
 */
export function loadKeystoreInMemory(keys) {
  memoryKeystore = {
    signKeyPair: keys.signKeyPair,
    boxKeyPair: keys.boxKeyPair,
    seedBuffer: keys.seedBuffer,
    loadedAt: Date.now(),
  };
}

export function getKeystoreFromMemory() {
  return memoryKeystore;
}

/**
 * Cancella tutto IDB del progetto (chiamato quando si entra in pure session).
 */
async function wipeIDB() {
  if (!window.indexedDB) return;
  const dbNames = ['nullum-keystore', 'firebaseLocalStorageDb', 'firebase-installations-database', 'firebase-messaging-database'];
  for (const name of dbNames) {
    try {
      await new Promise((resolve) => {
        const req = indexedDB.deleteDatabase(name);
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
        req.onblocked = () => resolve();
      });
    } catch (_e) { /* ignore */ }
  }
}

/**
 * Hook beforeunload: in pure session, prima di chiudere mostra warning.
 */
export function installPureSessionWarning() {
  if (!isPureSessionEnabled()) return;
  window.addEventListener('beforeunload', (e) => {
    if (memoryKeystore) {
      e.preventDefault();
      e.returnValue = 'Modalità pure session attiva. Chiudendo la pagina dovrai reinserire le 24 parole.';
      return e.returnValue;
    }
  });
}

/**
 * Quando l'utente esce volontariamente: cancella la memoria.
 */
export function clearMemorySession() {
  memoryKeystore = null;
}
